import { Section } from "../tools/section";
import Swiper from 'swiper';
import { Scrollbar, Navigation, EffectFade } from "swiper/modules";
import AOS from 'aos';
import 'aos/dist/aos.css';


// Selectors
const SELECTOR_SECTION =          '[data-featured-products]';
const SELECTOR_IMAGE_SWIPER =     '[data-swiper-image]';
const SELECTOR_PRODUCT_SWIPER =   '[data-swiper-product]';
const SELECTOR_VIDEO_SWIPER =     '[data-swiper-video]';
const SELECTOR_TITLE_SWIPER =     '[data-swiper-mobile-title]';
const SELECTOR_SWIPER_SCROLLBAR = '[data-swiper-scrollbar]';
const SELECTOR_PREV_IMG =         '[data-prev-image]';
const SELECTOR_NEXT_IMG =         '[data-next-image]';
const SELECTOR_SWIPER_NEXT =      '[data-swiper-next]';
const SELECTOR_SWIPER_PREV =      '[data-swiper-prev]';
const SELECTOR_IMAGE_WRAPPER =      '[data-image-wrapper]';
const SELECTOR_VIDEO_WRAPPER =      '[data-video-wrapper]';
const SELECTOR_PRODUCT_IMAGE =      '[data-product-image]';

export class FeaturedProducts extends Section {
  private imageSwiper: Swiper | null = null;
  private productSwiper: Swiper | null = null;
  private videoSwiper: Swiper | null = null;
  private titleSwiper: Swiper | null = null;
  private scrollbarEl: HTMLElement | null = null;
  

  private imageSwiperContainer: HTMLElement | null = null;
  private productSwiperContainer: HTMLElement | null = null;
  private videoSwiperContainer: HTMLElement | null = null;
  private titleSwiperContainer: HTMLElement | null = null;
  private prevImageEl: HTMLElement | null = null;
  private nextImageEl: HTMLElement | null = null;
  private prevEl: HTMLElement | null = null;
  private nextEl: HTMLElement | null = null;
  private resizeTimeout: number | null = null;
  private scrollListener: (() => void) | null = null;
  private imageWrapper : HTMLElement | null = null;
  private videoWrapper : HTMLElement | null = null;


  queryChildren(container: HTMLElement) {
    this.imageSwiperContainer = container.querySelector(SELECTOR_IMAGE_SWIPER);
    this.productSwiperContainer = container.querySelector(SELECTOR_PRODUCT_SWIPER);
    this.videoSwiperContainer = container.querySelector(SELECTOR_VIDEO_SWIPER);
    this.titleSwiperContainer = container.querySelector(SELECTOR_TITLE_SWIPER);
    this.prevImageEl = container.querySelector(SELECTOR_PREV_IMG);
    this.nextImageEl = container.querySelector(SELECTOR_NEXT_IMG);
    this.prevEl = container.querySelector(SELECTOR_SWIPER_PREV);
    this.nextEl = container.querySelector(SELECTOR_SWIPER_NEXT);
    this.scrollbarEl = container.querySelector(SELECTOR_SWIPER_SCROLLBAR);
    this.imageWrapper = container.querySelector(SELECTOR_IMAGE_SWIPER);
    this.videoWrapper = container.querySelector(SELECTOR_VIDEO_WRAPPER);
  }

  attachEvents() {
    this.enableSwipers();

    AOS.init({ duration: 800, easing: 'ease-out', once: true });
    
    requestAnimationFrame(() => {
      AOS.refreshHard();
    });

    this.updateAdjacentImages();
    this.setupVideoScroll();
    window.addEventListener('resize', this.handleResize);
  }

  detachEvents() {
    window.removeEventListener('resize', this.handleResize);
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }
  }

  handleResize = () => {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = window.setTimeout(() => {
      this.reinitializeSwipers();

      if (this.scrollListener) {
        window.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
      }

      this.setupVideoScroll();
    }, 300);
  };


  private reinitializeSwipers() {
    this.imageSwiper?.destroy(true, true);
    this.productSwiper?.destroy(true, true);
    this.videoSwiper?.destroy(true, true);
    this.titleSwiper?.destroy(true, true);

    this.imageSwiper = null;
    this.productSwiper = null;
    this.videoSwiper = null;
    this.titleSwiper = null;

    this.enableSwipers();
  }

  private enableSwipers() {
    if (
      !this.imageSwiperContainer ||
      !this.productSwiperContainer ||
      !this.videoSwiperContainer ||
      !this.titleSwiperContainer
    ) return;

    const isLargeScreen = window.innerWidth >= 1100;

    this.productSwiper = new Swiper(this.productSwiperContainer, {
      modules: [Scrollbar, EffectFade, Navigation],
      effect: isLargeScreen ? 'fade' : 'slide',
      fadeEffect: isLargeScreen ? { crossFade: true } : undefined,
      slidesPerView: isLargeScreen ? 1.5 : 1,
      speed: 500,
      spaceBetween: isLargeScreen ? 40 : 0,
      scrollbar: isLargeScreen
        ? false
        : {
            el: this.scrollbarEl,
            draggable: true,
          },
      navigation: isLargeScreen
        ? {
            nextEl: this.nextEl,
            prevEl: this.prevEl,
          }
        : false
    });


    this.imageSwiper = this.createBasicSwiper(this.imageSwiperContainer);
    this.videoSwiper = this.createBasicSwiper(this.videoSwiperContainer);
    this.titleSwiper = this.createBasicSwiper(this.titleSwiperContainer, true, true);
    this.updateAsideNoVideoClassOnActiveSlide();

    const syncSwipers = (index: number, source: 'image' | 'product' | 'video' | 'title') => {
      const swipers: Record<string, Swiper | null> = {
        image: this.imageSwiper,
        product: this.productSwiper,
        video: this.videoSwiper,
        title: this.titleSwiper,
      };

      Object.entries(swipers).forEach(([key, swiper]) => {
        if (key !== source && swiper && swiper.activeIndex !== index) {
          swiper.slideTo(index);
        }
      });
    };


    this.imageSwiper.on('slideChange', () => {
      syncSwipers(this.imageSwiper!.activeIndex, 'image');
    });
    this.productSwiper.on('slideChange', () => {
      syncSwipers(this.productSwiper!.activeIndex, 'product');
      this.updateAdjacentImages()
    });
    this.videoSwiper.on('slideChange', () => {
      syncSwipers(this.videoSwiper!.activeIndex, 'video');
      this.updateAsideNoVideoClassOnActiveSlide();
    });
    this.titleSwiper.on('slideChange', () => {
      syncSwipers(this.titleSwiper!.activeIndex, 'title');
    });
  }

  private updateAdjacentImages() {
    if (!this.productSwiper) return;

    const { slides, activeIndex } = this.productSwiper;

    const getImageSrc = (slide: HTMLElement): string | null => {
      const img = slide.querySelector<HTMLImageElement>(SELECTOR_PRODUCT_IMAGE);
      return img?.getAttribute('src') || null;
    };

    const prevIndex = activeIndex - 1 >= 0 ? activeIndex - 1 : null;
    const nextIndex = activeIndex + 1 < slides.length ? activeIndex + 1 : null;

    const prevSrc = prevIndex !== null ? getImageSrc(slides[prevIndex] as HTMLElement) : null;
    const nextSrc = nextIndex !== null ? getImageSrc(slides[nextIndex] as HTMLElement) : null;

    if (this.prevImageEl) {
      if (prevSrc) {
        this.resetAOSClasses(this.prevImageEl);
        this.prevImageEl.innerHTML = `<img src="${prevSrc}" alt="Previous product" data-aos="fade" data-aos-duration="700"/>`;
        this.prevImageEl.style.display = '';
      } else {
        this.prevImageEl.innerHTML = '';
      }
    }

    if (this.nextImageEl) {
      if (nextSrc) {
        this.resetAOSClasses(this.nextImageEl);
        this.nextImageEl.innerHTML = `<img src="${nextSrc}" alt="Next product" data-aos="fade" data-aos-duration="700"/>`;
        this.nextImageEl.style.display = '';
      } else {
        this.nextImageEl.innerHTML = '';
      }
    }
  }

  private resetAOSClasses(panel: HTMLElement) {
    panel.classList.remove('aos-animate', 'aos-init');
    // Clear on panel itself
    (panel as HTMLElement).style.transitionDelay = '';
    (panel as HTMLElement).style.animationDelay = '';

    panel.querySelectorAll<HTMLElement>('[data-aos]').forEach((node) => {
      node.classList.remove('aos-animate', 'aos-init');
      // Clear inline delays that AOS may have set previously
      node.style.transitionDelay = '';
      node.style.animationDelay = '';
    });
  }

  private createBasicSwiper(container: HTMLElement, useFadeEffect = false, autoHeight = false): Swiper {
    const config: any = {
      autoHeight: autoHeight,
      slidesPerView: 1,
      speed: 1000,
      touchMoveStopPropagation: true,
    };
    if (useFadeEffect) {
      config.modules = [EffectFade];
      config.effect = 'fade';
      config.fadeEffect = { crossFade: true };
    }
    return new Swiper(container, config);
  }

  private setupVideoScroll() {
    if (!this.imageWrapper || !this.videoWrapper) return;

    const section = this.imageWrapper;
    const videoWrapper = this.videoWrapper;
    const videoMargin = 64;
    const width = window.innerWidth;
  

    const applyStyles = (position: 'absolute' | 'fixed', top?: string, bottom?: string, right = '2px') => {
      videoWrapper.style.position = position;
      videoWrapper.style.top = top ?? 'auto';
      videoWrapper.style.bottom = bottom ?? 'auto';
      videoWrapper.style.transform = 'none';
      videoWrapper.style.right = width > 750 && width < 1100 ? '80px': right;
    };

    const onScroll = () => {
      const sectionRect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const videoHeight = videoWrapper.offsetHeight;

      const sectionTop = sectionRect.top;
      const sectionBottom = sectionRect.bottom;
      const stickyBottom = videoMargin;
      const videoBottomInViewport = viewportHeight - stickyBottom;

      const bottomBuffer = viewportHeight * 0.1;
      const topBuffer = viewportHeight * 0.01;

      const isAtBottom = sectionBottom <= (viewportHeight - stickyBottom + bottomBuffer);
      const isAtTop = (topBuffer + sectionTop + videoHeight + videoMargin) >= videoBottomInViewport;

      if (isAtBottom) {
        applyStyles('absolute', undefined, `${videoMargin}px`, width > 750 && width < 1100 ? '80px': '-45px');
      } else if (isAtTop) {
        applyStyles('absolute', `${videoMargin}px`, undefined, width > 750 && width < 1100 ? '80px': '-45px');
      } else {
        applyStyles('fixed', undefined, `${stickyBottom}px`, width > 750 && width < 1100 ? '80px': '2px');
      }
    };

    this.scrollListener = onScroll;
    window.addEventListener('scroll', onScroll);
    onScroll();
  }

  private updateAsideNoVideoClassOnActiveSlide() {
    if (!this.videoSwiper || !this.videoSwiperContainer) return;

    const activeSlide = this.videoSwiper.slides[this.videoSwiper.activeIndex];
    if (!activeSlide) return;

    const item = this.videoSwiperContainer.closest(
      ".c-featured-products__item"
    );
    if (!item) return;

    const aside = item.querySelector(".c-featured-products__aside");
    if (!aside) return;

    const hasVideo = activeSlide.querySelector("video, [data-video]") !== null;
    aside.classList.toggle("no-video", !hasVideo);
  }
}

Section.register(SELECTOR_SECTION, FeaturedProducts);
